import React, { useState, useEffect, useCallback } from 'react';
import './Statistics.css';
import { API_BASE_URL } from '../config';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface Baby {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  is_owner?: boolean;
  owner_username?: string;
}

interface SleepEntry {
  id: string;
  baby_id: string;
  start_time: string;
  end_time: string | null;
  created_at: string;
  is_ongoing: boolean;
  duration_minutes: number | null;
}

interface DailyTotal {
  date: string;
  dayName: string;
  totalMinutes: number;
  totalHours: number;
}

interface StatisticsProps {
  user: any;
}

const Statistics: React.FC<StatisticsProps> = ({ user }) => {
  const [babies, setBabies] = useState<Baby[]>([]);
  const [sleepEntries, setSleepEntries] = useState<SleepEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = last week, etc.
  const [dailyTotals, setDailyTotals] = useState<DailyTotal[]>([]);

  // Find active baby based on user's active_baby_id
  const activeBaby = babies.find(baby => baby.id === user.active_baby_id) || babies[0];

  const fetchBabies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setBabies(data);
      }
    } catch (error) {
      console.error('Failed to fetch babies:', error);
    }
  }, [user.id]);

  const fetchSleepEntries = useCallback(async () => {
    if (!activeBaby) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/baby/${activeBaby.id}`);
      if (response.ok) {
        const data = await response.json();
        setSleepEntries(data);
      }
    } catch (error) {
      console.error('Failed to fetch sleep entries:', error);
    } finally {
      setLoading(false);
    }
  }, [activeBaby]);

  useEffect(() => {
    if (user) {
      fetchBabies();
    }
  }, [user, fetchBabies]);

  useEffect(() => {
    if (activeBaby) {
      fetchSleepEntries();
    }
  }, [activeBaby, fetchSleepEntries]);

  const getWeekDates = useCallback(() => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay; // Adjust so week starts on Monday

    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset + (weekOffset * 7));
    monday.setHours(0, 0, 0, 0);

    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(date);
    }

    return dates;
  }, [weekOffset]);

  const calculateDailyTotals = useCallback(() => {
    const weekDates = getWeekDates();
    const totals: DailyTotal[] = weekDates.map(date => {
      const dateStr = date.toDateString();
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });

      // Filter sleep entries for this day
      const dayEntries = sleepEntries.filter(entry => {
        const entryDate = new Date(entry.start_time);
        return entryDate.toDateString() === dateStr;
      });

      // Calculate total sleep minutes for this day
      const totalMinutes = dayEntries.reduce((sum, entry) => {
        return sum + (entry.duration_minutes || 0);
      }, 0);

      return {
        date: dateStr,
        dayName,
        totalMinutes,
        totalHours: parseFloat((totalMinutes / 60).toFixed(1))
      };
    });

    setDailyTotals(totals);
  }, [sleepEntries, getWeekDates]);

  useEffect(() => {
    if (sleepEntries.length > 0) {
      calculateDailyTotals();
    }
  }, [sleepEntries, calculateDailyTotals]);

  const getWeekRangeLabel = () => {
    const weekDates = getWeekDates();
    const start = weekDates[0];
    const end = weekDates[6];

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const year = start.getFullYear();
    return `${formatDate(start)} - ${formatDate(end)}, ${year}`;
  };

  const goToPreviousWeek = () => {
    setWeekOffset(prev => prev - 1);
  };

  const goToNextWeek = () => {
    setWeekOffset(prev => prev + 1);
  };

  const isCurrentWeek = () => weekOffset === 0;

  const formatTooltip = (value: number) => {
    const hours = Math.floor(value);
    const minutes = Math.round((value - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-day">{data.dayName}</p>
          <p className="tooltip-value">{formatTooltip(data.totalHours)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="statistics-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const maxHours = Math.max(...dailyTotals.map(d => d.totalHours), 12); // At least 12 hours for scale

  return (
    <div className="statistics-page">
      <div className="statistics-header">
        <h2>Sleep Statistics</h2>
        <p className="baby-name">{activeBaby?.name || 'Loading...'}</p>
      </div>

      <div className="week-navigation">
        <button
          className="week-nav-btn"
          onClick={goToPreviousWeek}
          aria-label="Previous week"
        >
          ◄
        </button>
        <div className="week-range">
          {getWeekRangeLabel()}
          {isCurrentWeek() && <span className="current-week-badge">Current Week</span>}
        </div>
        <button
          className="week-nav-btn"
          onClick={goToNextWeek}
          disabled={isCurrentWeek()}
          aria-label="Next week"
        >
          ►
        </button>
      </div>

      <div className="chart-container">
        <h3>Total Sleep Per Day</h3>
        {dailyTotals.every(d => d.totalMinutes === 0) ? (
          <div className="no-data">
            <p>No sleep data for this week</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={dailyTotals}
              margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
              <XAxis
                dataKey="dayName"
                stroke="#cccccc"
                style={{ fontSize: '0.9rem' }}
              />
              <YAxis
                stroke="#cccccc"
                style={{ fontSize: '0.9rem' }}
                label={{ value: 'Hours', angle: -90, position: 'insideLeft', fill: '#cccccc' }}
                domain={[0, Math.ceil(maxHours)]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="totalHours" radius={[8, 8, 0, 0]}>
                {dailyTotals.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.totalMinutes > 0 ? '#7c73ff' : 'rgba(124, 115, 255, 0.2)'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="statistics-summary">
        <div className="summary-card">
          <div className="summary-value">
            {formatTooltip(dailyTotals.reduce((sum, d) => sum + d.totalHours, 0))}
          </div>
          <div className="summary-label">Total This Week</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">
            {formatTooltip(
              dailyTotals.reduce((sum, d) => sum + d.totalHours, 0) / 7
            )}
          </div>
          <div className="summary-label">Average Per Day</div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
